import {
    Multiselect,
    MultiselectProps,
    Select,
    SelectProps
} from '@cloudscape-design/components';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalState } from '../../pages/context/GlobalStateContext';
import { ItemBelt } from '../../resources/types';


interface SelectBeltSingleProps {
    single: true; //just true
    value?: number; // Single ID
    width?: number;
    onSelect?: (selected: number | null) => void; // <--- CHANGED: Returns ID or null
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
}

// When single = false (or undefined, as per union type)
interface SelectBeltMultiProps {
    single?: false; // default is "false"
    value?: number[]; // Array of IDs
    width?: number;
    onSelect?: (selected: number[]) => void; // <--- CHANGED: Returns array of IDs
    readonly?: boolean;
    tokenLimit?: number;
    hideTokens?: boolean;
}

export type SelectBeltProps =
    | SelectBeltSingleProps
    | SelectBeltMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectBelt: React.FC<SelectBeltProps> = (props) => {
    const {
        single,
        value,
        width = 200,
        readonly,
        tokenLimit = 3,
        hideTokens = false,
        onSelect
    } = props;

    const { data } = useGlobalState();
    if (!data || !data.belts) return <></>;

    // State for Single Selection (holds the selected object)
    const [selectedSingle, setSelectedSingle] = useState<ItemBelt | null>(null);

    // State for Multi Selection (holds the array of selected objects)
    const [selectedMulti, setSelectedMulti] = useState<ItemBelt[]>([]);


    // --- Mapped Categories (Options List) ---

    // Memoize the calculation of available options
    const mappedBelts: ItemBelt[] = useMemo(() => {
        let belts: ItemBelt[] = data.belts
            .filter((plan: ItemBelt) => 1 === 1)
            .map((plan: ItemBelt) => ({
                idBelt: plan.idBelt,
                belt: plan.belt,
                value: plan.idBelt.toString(),
                label: plan.belt
            }));
        return belts;
    }, [data.belts]);

    // --- Initialization & Value Sync Effect ---

    useEffect(() => {
        if (!value) {
            setSelectedSingle(null);
            setSelectedMulti([]);
            return;
        }

        if (single) {
            // SINGLE MODE: Value is a number (ID)
            const id = value as number;
            const match = mappedBelts.find(belt => belt.idBelt === id) || null;
            setSelectedSingle(match);

        } else {
            // MULTI MODE: Value is an array of numbers (IDs)
            const ids = value as number[];
            const matches = mappedBelts.filter(belt => ids.includes(belt.idBelt));
            setSelectedMulti(matches);
        }
    }, [value, single, mappedBelts]);


    // --- Single Select Handler ---
    const handleSingleChange: SelectProps['onChange'] = ({ detail }) => {
        const selectedOption = detail.selectedOption as ItemBelt | null;

        setSelectedSingle(selectedOption);

        if (onSelect && single) {
            const selectedId = selectedOption?.idBelt ?? null; // Extract ID
            // FIX: Assert the function signature directly to satisfy the compiler
            (onSelect as (selected: number | null) => void)(selectedId);
        }
    };

    // --- Multi Select Handler ---
    const handleMultiChange: MultiselectProps['onChange'] = ({ detail }) => {
        const list = detail.selectedOptions as ItemBelt[];

        // Use spread to ensure setting a new array reference
        setSelectedMulti([...list]);

        if (onSelect && !single) {
            const selectedIds = list.map(item => item.idBelt); // Extract array of IDs
            // FIX: Assert the function signature directly to satisfy the compiler
            (onSelect as (selected: number[]) => void)(selectedIds);
        }
    };

    // --- Rendering ---

    const placeholder = single ? "Seleccionar cinta" : "Seleccionar cintas";
    const wrapperStyle: React.CSSProperties = {
        width: width > 0 ? `${width}px` : '100%'
    };
    return (
        <div style={wrapperStyle}>
            {single ? (
                // SINGLE SELECTION
                <Select
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOption={selectedSingle}
                    options={mappedBelts}
                    onChange={handleSingleChange}
                />
            ) : (
                // MULTI SELECTION
                <Multiselect
                    tokenLimit={tokenLimit}
                    hideTokens={hideTokens}
                    readOnly={readonly}
                    placeholder={selectedMulti.length === 0 ? placeholder : `${selectedMulti.length}/${mappedBelts.length} seleccionadas`}
                    selectedOptions={selectedMulti}
                    options={mappedBelts}
                    onChange={handleMultiChange}
                />
            )}

        </div>
    );
};

export default SelectBelt;